import React, { useState } from 'react';
import { useTheme } from '../../theme/ThemeContext';
import { usePriceSubscription } from '../../hooks/usePriceSubscription';
import { CloseIcon } from '../../helpers/Icons';
import {
  calcSingleProfit,
  getOrderCurrentPrice,
  getOrderOpenPrice,
  getOrderTypeLabel,
  getOrderVolumeLots,
  isBuyOrder,
  isPendingOrder,
  partialClosePosition,
} from '../../services/platform/TradingStore';
import {
  formatDateTime,
  formatPrice,
  getBalanceHistoryLabel,
  getOrderSL,
  getOrderTP,
  getOrderTimestamp,
  getTicketValue,
  toNumber,
} from './portfolioUtils';

const DetailRow = ({ label, value, theme }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: `1px solid ${theme.border}44` }}>
    <span style={{ fontSize: 12, color: theme.textM }}>{label}</span>
    <span style={{ fontSize: 12, fontWeight: 700, color: theme.text, fontFamily: theme.mono }}>{value}</span>
  </div>
);

const OrderDetailModal = ({ order, onClose, state = 'history' }) => {
  const theme = useTheme();
  const marketPrice = usePriceSubscription(order.symbol);
  const typeLabel = getOrderTypeLabel(order);
  const typeColor = isBuyOrder(order) ? theme.pos : theme.neg;
  const openPrice = toNumber(order.calculatedOpenPrice) || getOrderOpenPrice(order);
  const currentPrice = toNumber(order.calculatedClosePrice) || getOrderCurrentPrice(order);
  const livePrice = marketPrice ? (isBuyOrder(order) ? marketPrice.bid : marketPrice.ask) : (currentPrice || openPrice);
  const liveProfit = marketPrice ? calcSingleProfit(order, livePrice) : toNumber(order.calculatedProfit ?? order.profit ?? order.netProfit);
  const bookedProfit = toNumber(order.calculatedProfit ?? order.profit ?? order.netProfit);
  const hasHistoryOpenPrice = Boolean(toNumber(
    order.calculatedOpenPrice ??
    order.open_price ??
    order.openPrice ??
    order.price_open ??
    order.priceOpen,
  ));
  const sl = getOrderSL(order);
  const tp = getOrderTP(order);
  const openedAt = getOrderTimestamp(order);
  const updatedAt = state === 'history'
    ? (order.timeMsc || order.time_msc || order.time)
    : (order.timeUpdateMsc || order.timeUpdate || order.modified_at || order.created_at);
  const historyTime = order?.timeMsc;
  const totalVolume = getOrderVolumeLots(order);
  const [partialVolume, setPartialVolume] = useState(
    Math.max(0.01, totalVolume / 2).toFixed(2),
  );
  const [partialCloseState, setPartialCloseState] = useState({
    loading: false,
    error: "",
  });

  const handlePartialClose = async () => {
    const volume = Number(partialVolume);
    if (!Number.isFinite(volume) || volume <= 0 || volume >= totalVolume) {
      setPartialCloseState({
        loading: false,
        error: `Volume must be greater than 0 and less than ${totalVolume.toFixed(2)} lots.`,
      });
      return;
    }

    setPartialCloseState({ loading: true, error: "" });
    const result = await partialClosePosition(order, volume);
    if (result?.success === false || result?.status === false) {
      setPartialCloseState({
        loading: false,
        error: result?.message || "Partial close failed.",
      });
      return;
    }
    onClose();
  };

  if (order.isBalanceHistory) {
    const label = getBalanceHistoryLabel(order);

    return (
      <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, backdropFilter: 'blur(4px)' }}>
        <div style={{ background: theme.card, borderRadius: 16, width: '90%', maxWidth: 400, border: `1px solid ${theme.border}`, overflow: 'hidden', boxShadow: '0 30px 60px rgba(0,0,0,0.5)' }}>
          <div style={{ padding: '16px 20px', borderBottom: `1px solid ${theme.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: theme.card2 }}>
            <span style={{ fontSize: 14, fontWeight: 800, color: theme.text }}>History Details</span>
            <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}><CloseIcon s={16} c={theme.textM} /></button>
          </div>
          <div style={{ padding: 20 }}>
            <div style={{ textAlign: 'center', marginBottom: 20 }}>
              <div style={{ fontSize: 20, fontWeight: 800, color: theme.text, marginBottom: 4 }}>{label}</div>
              <span style={{ fontSize: 10, fontWeight: 800, color: bookedProfit >= 0 ? theme.pos : theme.neg, background: `${bookedProfit >= 0 ? theme.pos : theme.neg}15`, padding: '2px 8px', borderRadius: 6, textTransform: 'uppercase' }}>
                Balance
              </span>
            </div>

            <DetailRow theme={theme} label="Ticket" value={`#${getTicketValue(order)}`} />
            <DetailRow theme={theme} label="Comment" value={order.comment || 'Balance operation'} />
            <DetailRow theme={theme} label="Time" value={formatDateTime(historyTime)} />

            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '15px 0', marginTop: 5 }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: theme.text }}>Amount</span>
              <span style={{ fontSize: 16, fontWeight: 800, color: bookedProfit >= 0 ? theme.pos : theme.neg, fontFamily: theme.mono }}>
                {bookedProfit >= 0 ? '+' : ''}{bookedProfit.toFixed(2)}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, backdropFilter: 'blur(4px)' }}>
      <div style={{ background: theme.card, borderRadius: 16, width: '90%', maxWidth: 400, border: `1px solid ${theme.border}`, overflow: 'hidden', boxShadow: '0 30px 60px rgba(0,0,0,0.5)' }}>
        <div style={{ padding: '16px 20px', borderBottom: `1px solid ${theme.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: theme.card2 }}>
          <span style={{ fontSize: 14, fontWeight: 800, color: theme.text }}>Order Details</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}><CloseIcon s={16} c={theme.textM} /></button>
        </div>
        <div style={{ padding: 20 }}>
          <div style={{ textAlign: 'center', marginBottom: 20 }}>
            <div style={{ fontSize: 20, fontWeight: 800, color: theme.text, marginBottom: 4 }}>{order.symbol}</div>
            <span style={{ fontSize: 10, fontWeight: 800, color: typeColor, background: `${typeColor}15`, padding: '2px 8px', borderRadius: 6, textTransform: 'uppercase' }}>{typeLabel}</span>
          </div>

          <DetailRow theme={theme} label="Ticket" value={`#${getTicketValue(order)}`} />
          <DetailRow theme={theme} label="Volume" value={`${getOrderVolumeLots(order).toFixed(2)} lots`} />
          {(state !== 'history' || hasHistoryOpenPrice) && (
            <DetailRow theme={theme} label="Open Price" value={formatPrice(openPrice, order.digits ?? 5)} />
          )}
          {state === 'open' && <DetailRow theme={theme} label="Current Price" value={formatPrice(livePrice, order.digits ?? 5)} />}
          {state === 'history' && <DetailRow theme={theme} label="Close Price" value={formatPrice(currentPrice, order.digits ?? 5)} />}
          <DetailRow theme={theme} label="Stop Loss" value={sl > 0 ? formatPrice(sl, order.digits ?? 5) : 'Not set'} />
          <DetailRow theme={theme} label="Take Profit" value={tp > 0 ? formatPrice(tp, order.digits ?? 5) : 'Not set'} />
          {state === 'open' && <DetailRow theme={theme} label="Opened" value={formatDateTime(openedAt)} />}
          <DetailRow theme={theme} label="Updated" value={formatDateTime(updatedAt)} />
          <DetailRow theme={theme} label="Commission" value={order.commission || 0} />
          <DetailRow theme={theme} label="Swap" value={order.swap || 0} />

          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '15px 0', marginTop: 5 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: theme.text }}>Profit</span>
            <span style={{ fontSize: 16, fontWeight: 800, color: bookedProfit >= 0 ? theme.pos : theme.neg, fontFamily: theme.mono }}>
              {state === 'open'
                ? `${liveProfit >= 0 ? '+' : ''}${liveProfit.toFixed(2)}`
                : `${bookedProfit >= 0 ? '+' : ''}${bookedProfit.toFixed(2)}`}
            </span>
          </div>

          {state === 'open' && !isPendingOrder(order) && totalVolume > 0.01 && (
            <div style={{ borderTop: `1px solid ${theme.border}`, paddingTop: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: theme.text, marginBottom: 7 }}>
                Partial close
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  aria-label="Partial close volume"
                  type="number"
                  min="0.01"
                  max={Math.max(0.01, totalVolume - 0.01)}
                  step="0.01"
                  value={partialVolume}
                  onChange={event => setPartialVolume(event.target.value)}
                  style={{
                    minWidth: 0,
                    flex: 1,
                    borderRadius: 7,
                    border: `1px solid ${theme.border}`,
                    background: theme.card2,
                    color: theme.text,
                    padding: '8px 10px',
                    fontFamily: theme.mono,
                  }}
                />
                <button
                  type="button"
                  disabled={partialCloseState.loading}
                  onClick={handlePartialClose}
                  style={{
                    border: 'none',
                    borderRadius: 7,
                    padding: '8px 12px',
                    cursor: partialCloseState.loading ? 'wait' : 'pointer',
                    background: theme.orange,
                    color: '#fff',
                    fontSize: 11,
                    fontWeight: 700,
                  }}
                >
                  {partialCloseState.loading ? 'Closing…' : 'Close volume'}
                </button>
              </div>
              {partialCloseState.error && (
                <div style={{ color: theme.neg, fontSize: 10, marginTop: 6 }}>
                  {partialCloseState.error}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrderDetailModal;
